import { CampaignQuotaDisplay } from '@/ui/constructs/campaign/campaign-quota-display'
import { MainNav } from '@/ui/core/main-nav'
import { Button } from '@/ui/rapid-ui/button'
import { ErrorBox } from '@/ui/rapid-ui/error-box'
import { SearchFilter } from '@/ui/rapid-ui/filters/search-filter'
import { SelectFilter } from '@/ui/rapid-ui/filters/select-filter'
import { Glyphs } from '@/ui/rapid-ui/glyphs'
import { SubtleLoader } from '@/ui/rapid-ui/subtle-loader'
import { ViewToggle } from '@/ui/rapid-ui/view-toggle'
import { logger } from '@/ui/utilities/instrumentation/logger'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CampaignDashboardCard } from './campaign-dashboard-card'
import { CampaignGrid } from './campaign-grid'
import { CampaignTable } from './campaign-table'
import type { DashboardViewModel } from './dashboard.vm'
import { EmptyState } from './empty-state'
import { OverdueInvoicesAlert } from './overdue-invoices-alert'
import { WhatsAppAiConsentAlert } from './whatsapp-ai-consent-alert'
import { WhatsAppInactivityAlert } from './whatsapp-inactivity-alert'

type ViewMode = 'grid' | 'table'

const wrapperVariants = {
	initial: { opacity: 0 },
	animate: { opacity: 1 },
	exit: { opacity: 0 },
}

export function Dashboard({
	view,
	events,
	renderers,
	loading,
	errors,
}: DashboardViewModel) {
	const { t } = useTranslation('common')
	const [selectedCampaignStatuses, setSelectedCampaignStatuses] = useState<string>('all')
	const [searchQuery, setSearchQuery] = useState<string>('')
	const [viewMode, setViewMode] = useState<ViewMode>('grid')

	useEffect(() => {
		logger.register({ dashboard_view_mode: viewMode })
		return () => logger.unregister('dashboard_view_mode')
	}, [viewMode])

	const viewModeOptions = useMemo(() => [
		{ value: 'grid' as ViewMode, label: t('dashboard.viewMode.grid'), icon: <Glyphs.core.GridIcon /> },
		{ value: 'table' as ViewMode, label: t('dashboard.viewMode.table'), icon: <Glyphs.core.TableIcon /> },
	], [t])

	const campaignStatusOptions = useMemo(() =>
		[
			{ id: 'all', label: t('dashboard.status.allActive') },
			{ id: 'draft', label: t('dashboard.status.draft') },
			{ id: 'review', label: t('dashboard.status.inReview') },
			{ id: 'completed', label: t('dashboard.status.completed') },
			{ id: 'active', label: t('dashboard.status.active') },
			{ id: 'separator', label: '', separator: true },
			{ id: 'archived', label: t('dashboard.status.allArchived') },
		] as const, [t])

	const handleStatusChange = (status: string) => {
		setSelectedCampaignStatuses(status)
		events.onStatusFilterChange(status)
	}

	const hasActiveFilters = searchQuery !== '' || selectedCampaignStatuses !== 'all'

	const getFilterStatusText = () => {
		const filterTextMap: Record<string, string> = {
			all: t('dashboard.filterText.all'),
			archived: t('dashboard.filterText.archived'),
			draft: t('dashboard.filterText.draft'),
			review: t('dashboard.filterText.review'),
			completed: t('dashboard.filterText.completed'),
			active: t('dashboard.filterText.active'),
		}
		return filterTextMap[selectedCampaignStatuses] ?? t('dashboard.filterText.all')
	}

	const filteredCampaigns = useMemo(() => {
		return view.campaigns.filter((c) => {
			const matchesSearch = c.title.toLowerCase().includes(searchQuery.toLowerCase())

			if (selectedCampaignStatuses === 'archived') {
				// In archived mode, view.campaigns already contains only archived campaigns
				return matchesSearch
			}

			if (selectedCampaignStatuses === 'all') {
				return matchesSearch && !c.isArchived
			}

			return c.status === selectedCampaignStatuses && matchesSearch && !c.isArchived
		})
	}, [view.campaigns, selectedCampaignStatuses, searchQuery])

	// Determine view states
	const isArchivedMode = selectedCampaignStatuses === 'archived'
	const isDefaultMode = selectedCampaignStatuses === 'all'

	// The user has never created any campaigns (show welcome/empty state)
	const hasNoCampaignsInSystem = view.campaigns.length === 0 && isDefaultMode

	// The user has campaigns, but none match current filters OR we're in archived mode with no results(show reset option)
	const hasNoFilteredResults = (view.campaigns.length > 0 && filteredCampaigns.length === 0)
		|| (isArchivedMode && view.campaigns.length === 0)

	// Show the filter bar whenever we're not in the "no campaigns ever" state
	const shouldShowFilterBar = !hasNoCampaignsInSystem

	// Show campaign cards when we have filtered results
	const shouldShowCampaignGrid = filteredCampaigns.length > 0

	return (
		<MainNav activeMainNavLink="campaigns">
			<div className="flex flex-col items-center gap-12 ">
				{errors.campaigns && (
					<motion.div key="campaigns" variants={wrapperVariants}>
						<ErrorBox error={errors.campaigns} />
					</motion.div>
				)}

				{!errors.campaigns && (
					<AnimatePresence mode="popLayout">
						{!errors.campaigns && loading.campaigns
							? (
								<div className="min-h-40 flex items-center justify-center">
									<SubtleLoader delay={0} />
								</div>
							)
							: (
								<motion.div
									key="campaigns"
									variants={wrapperVariants}
									className="w-full px-20"
								>
									<WhatsAppInactivityAlert
										campaigns={isArchivedMode ? null : view.campaigns}
										className="mx-auto mt-6 w-full max-w-[1440px]"
									/>
									<WhatsAppAiConsentAlert className="mx-auto mt-6 w-full max-w-[1440px]" />

									{/* Welcome screen for brand new users */}
									{hasNoCampaignsInSystem && <EmptyState userFirstName={view.userFirstName} />}

									{/* Filter bar - always visible when user has campaigns */}
									{shouldShowFilterBar && (
										<div className="flex flex-col items-center w-full pt-6 pb-6">
											<div
												className="w-full flex flex-col gap-4"
												style={{
													maxWidth: `1440px`,
												}}
											>
												<OverdueInvoicesAlert />
												<CampaignQuotaDisplay />
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-4">
														<SearchFilter
															value={searchQuery}
															onChange={setSearchQuery}
															placeholder={t('dashboard.searchPlaceholder')}
															theme="floating"
															showShortcut
														/>
														<SelectFilter
															options={campaignStatusOptions}
															value={selectedCampaignStatuses}
															onChange={handleStatusChange}
															placeholder={t('dashboard.filterPlaceholder')}
															theme="floating"
														/>
														<span className="text-sm text-grey-500">
															{getFilterStatusText()}
														</span>
													</div>
													<ViewToggle
														value={viewMode}
														onChange={setViewMode}
														options={viewModeOptions}
													/>
												</div>
											</div>
										</div>
									)}

									{/* Campaign display - shown when we have matching campaigns */}
									{shouldShowCampaignGrid && viewMode === 'grid' && (
										<CampaignGrid
											data={filteredCampaigns.slice().reverse()}
											showPlaceholders={!hasActiveFilters}
											renderCard={(c, i) => (
												<CampaignDashboardCard
													campaign={c}
													i={filteredCampaigns.length - i}
													key={`${c.id}-${c.status}`}
													linkRenderer={renderers.campaignLink}
													onArchive={events.onArchiveCampaign}
													onUnarchive={events.onUnarchiveCampaign}
													onCopy={events.onCopyCampaign}
													isArchiving={view.archivingCampaignIds.includes(c.id)}
													isUnarchiving={view.unarchivingCampaignIds.includes(c.id)}
													isCopying={view.copyingCampaignIds.includes(c.id)}
												/>
											)}
											pageMaxWidth={1440}
										/>
									)}

									{shouldShowCampaignGrid && viewMode === 'table' && (
										<CampaignTable
											campaigns={filteredCampaigns.slice().reverse()}
											linkRenderer={renderers.campaignLink}
											onArchive={events.onArchiveCampaign}
											onUnarchive={events.onUnarchiveCampaign}
											onCopy={events.onCopyCampaign}
											archivingCampaignIds={view.archivingCampaignIds}
											unarchivingCampaignIds={view.unarchivingCampaignIds}
											copyingCampaignIds={view.copyingCampaignIds}
										/>
									)}

									{/* No matches - offer to reset filters */}
									{hasNoFilteredResults && (
										<div className="flex items-center justify-center flex-col gap-4 mt-30">
											<div className="bg-white p-6 rounded-md shadow-sm flex flex-col items-center justify-between gap-4">
												<div className="text-grey-800 text-lg font-medium">
													{t('dashboard.noResults')}
												</div>
												<Button
													type="colorful"
													size="short"
													onClick={() => {
														setSelectedCampaignStatuses('all')
														setSearchQuery('')
														events.onStatusFilterChange('all')
													}}
												>
													{t('dashboard.resetFilters')}
												</Button>
											</div>
										</div>
									)}
								</motion.div>
							)}
					</AnimatePresence>
				)}
			</div>
		</MainNav>
	)
}
